import confirm from 'components/dialogConfirm'
import { isSequenceLive, WorkflowStatus } from 'models/campaign'

/**
 * FIR-7549: removing a company from a campaign also removes its contacts
 * from the campaign's email sequence (enforced server-side on every removal
 * path). When the sequence is live this stops future sends, so every UI
 * surface that can remove a company must warn and get explicit confirmation.
 */

/** Minimal campaign shape needed to decide whether removal touches a live sequence. */
export interface RemovableCampaignInfo {
  id: number
  name?: string
  workflow_status?: WorkflowStatus | null
}

/**
 * Shows the live-sequence removal warning. Resolves true when the user
 * confirms. Copy notes that already-sent emails are unaffected; removal
 * only prevents future sends.
 */
export async function confirmRemoveFromLiveSequence(options: {
  companyCount?: number
  campaignName?: string
}): Promise<boolean> {
  const count = options.companyCount ?? 1
  const subject = count === 1 ? 'this company' : `these ${count} companies`
  const possessive = count === 1 ? 'its' : 'their'
  const target = options.campaignName
    ? `"${options.campaignName}"`
    : 'this campaign'
  const confirmed = await confirm(
    `Removing ${subject} from ${target} will remove ${possessive} contacts ` +
      'from an active sequence. Contacts who already received emails will ' +
      'not be sent any further emails. Are you sure?',
    'Remove from active sequence?',
    '',
    { confirmLabel: 'Remove', buttonVariant: 'danger' }
  )
  return Boolean(confirmed)
}

/**
 * Diffs a campaigns form edit (previous ids vs next ids) and returns the
 * campaigns being removed whose sequence is live.
 */
export function findRemovedLiveCampaigns(
  previousIds: number[],
  nextIds: number[],
  campaigns: RemovableCampaignInfo[]
): RemovableCampaignInfo[] {
  const next = new Set(nextIds)
  return campaigns.filter(
    (c) =>
      previousIds.includes(c.id) &&
      !next.has(c.id) &&
      isSequenceLive(c.workflow_status)
  )
}
